import { LitElement, html, css } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { Feature, MapBrowserEvent, View } from 'ol';
import { transform, fromLonLat, toLonLat } from 'ol/proj';
import { ScaleLine } from 'ol/control';
import { Units } from 'ol/control/ScaleLine';
import MGPlayerCore from '@meteogroup/player-core';
import { Map as MGMap } from '@meteogroup/map-layers';
import { INFINITE_EXTENT } from '@meteogroup/map-layers/proj/epsg3395';

import { MgLayers } from '../../../src/maplayers/mg-layers';
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils';
import { FiSubView, UiStateModel, UiStateModelParam } from '../../../src/models/ui-state-model';
import { Layer } from 'ol/layer';
import { Coordinate } from 'ol/coordinate';
import { VoyageLayers } from '../../../src/maplayers/voyage-layers';
import { defaults as interactionDefaults } from 'ol/interaction';
import { MouseWheelZoom } from 'ol/interaction';
import { Options } from 'ol/interaction/MouseWheelZoom';
import { Source } from 'ol/source';
import { Geometry } from 'ol/geom';
import RenderFeature from 'ol/render/Feature';
import { FI_EVENT_CATEGORIES, MAP_INTERACTIONS, TraceService } from '../../../src/services/trace-service';
import { AuthService, AuthorizationRoles } from '../../../src/services/auth-service';
import { VoyageRoutingEvents, VoyageRoutingModel } from '../../../src/models/voyage-routing-model';

const isVessel = (<any>window)["appenv"].vesselIMONumber !== undefined;
const _PROJECTION = (<any>window)["appenv"].projection;

interface MapAtInfo
{
    longitude: number;
    latitude: number;
    zoom: number;
}

export enum CustomMapEvents {
    EndOfAnimation = "end-of-animation"
}

@customElement('voyage-ol-map')
export class VoyageOLMap extends LitElement {
    constructor() {
        super();

        this._fetchMapStyle();
        let _isuSerInteranl = AuthService.userHasRole(AuthorizationRoles.MaerskInternal)
        if(!_isuSerInteranl) this._mapAt = { latitude: 0, longitude: 0, zoom:3 };
    }

    static get styles() {
        return [
            css`
            :host {
                margin: 0;
                padding: 0;
            }
            #map {
                width: 100%;
                height: 100%;
            }

            #infobox {
                font-family: var(--component-heading-ff, "serif");
                font-size: 11px;
                position: absolute;
                left: 0;
                top: 0;
                width:auto;
                height:auto;
                background: rgba(0,0,0,0.7);
                color: white;
                z-index: 1000;
                padding: 0.5em;
                display: none;
                transform: translate(-50%, -120%);
            }

            hr {
                border-top: 1px dashed white;
            }

            .ol-scale-line {
                left: unset;
                right: 60px;
                top: unset;
                bottom: 15px;
            }

            .disclaimer {
                position: absolute;
                background-color: var(--neutrals-grey-07, LightGrey);
                padding: 0 2px 0 2px;
                z-index: 1;
                left: unset;
                right: 100px;
                top: unset;
                bottom: 15px; 
                color: red;
                font-size: 12px;
            }

            /* if there is a disclaimer element, then change position of ol-scale-line */
            .disclaimer ~ div .ol-scale-line {
                bottom: 35px;
            }

            /* classname is set in weather-broadcasting-layer.ts on the dragbox interaction */
            .drag-box { 
                background-color: rgba(var(--primary-blue-900-RGB, blue), 0.1);
                border-color: var(--primary-blue-900-RGB, blue);
            }
            `
        ];
    }

    render() {
        return html`
        <style>${this._mapStyles}</style>
        <div id="map">
            <div id="infobox">
                <span id="info">${this._infotext.map(line => html`<div>${line}</div>`)}</span>
            </div>
            ${isVessel ? html`<div class="disclaimer">DISCLAIMER: Application not intended for navigation</div>` : html``}
        </div>
        `;
    }

    initMaps(player: MGPlayerCore, mapLayers: Layer<Source>[], voyageLayers?: VoyageLayers) {

        if (voyageLayers)
        {
            this._voyageLayers = voyageLayers;
            this._voyageLayers.addEventListener('center-at', <EventListener>((e: CustomEvent) => {
                this._mapView!.cancelAnimations();
                this._mapView!.animate({
                    center: e.detail,
                    duration: 800
                }, () => this._map?.dispatchEvent(CustomMapEvents.EndOfAnimation));
            }));
        }

        VoyageRoutingModel?.addEventListener(VoyageRoutingEvents.CenterWarningCoordinates, (e: Event) => {
            this.centerOn((<CustomEvent>e)?.detail?.coordinates, true);
            this._mapView?.setZoom(7);
        });

        const atStr = UiStateModel!.getValue('at');
        if (atStr) {
            try {
                const tmpAt = atStr.split(',').map(v => Number.parseFloat(v));
                if (tmpAt.length === 3) {
                    this._mapAt = {
                        latitude: tmpAt[0],
                        longitude: tmpAt[1],
                        zoom: tmpAt[2]
                    };
                }
            } catch (error) {
                console.warn('Error parsing map location and zoom');
            }
        }

        const setURLMapAt = () => {
            UiStateModel!.setValue('at', `${this._mapAt.latitude.toFixed(7)},${this._mapAt.longitude.toFixed(7)},${this._mapAt.zoom.toFixed(2)}z`);
        }

        this._mapView = new View({
            projection: _PROJECTION,
            center: fromLonLat([this._mapAt.longitude, this._mapAt.latitude], _PROJECTION),
            zoom: this._mapAt.zoom,
            constrainResolution: true,
        });

        this._mapView.on('change:resolution', () => {
            const zoom = this._mapView?.getZoom();

            this._mapAt.zoom = zoom ? zoom : this._mapAt.zoom;

            MgLayers!.zoomLevelChanged(this._mapAt.zoom);

            setURLMapAt();
        });

        this._mapView.on('change:center', () => {
            const center = this._mapView?.getCenter();
            if (center) {
                const coords = toLonLat(center, _PROJECTION);

                this._mapAt.longitude = coords[0];
                this._mapAt.latitude = coords[1];

                setURLMapAt();
            }
        });

        const projection = this._mapView.getProjection();
        projection.setGlobal(true);

        const scaleLineControl = new ScaleLine();
        scaleLineControl.setUnits('nautical');
        let interactions = interactionDefaults();

        // overriding the default mouse wheel zoom behavior, but still relying on default interactions
        for (let i = 0; i < interactions.getLength(); i++) {
            let o = interactions.item(i);

            if (o instanceof MouseWheelZoom) {
                interactions.setAt(i, new MouseWheelZoom(<Options>{constrainResolution: true}))
            }
        }


        this._map = new MGMap({player,
            controls: [
                scaleLineControl
            ],
            interactions,
            target: this._mapElement,
            layers: mapLayers,
            view: this._mapView,
            restrictedExtent: INFINITE_EXTENT,
            loadTilesWhileAnimating: true,
            loadTilesWhileInteracting: true,
            tileRefreshDebounce: 200,
            pixelRatio: 1
        });

        const infoBox = <HTMLDivElement>this.shadowRoot!.querySelector('#infobox');

        // Split this up in move and click (traversing features seems a bit heavy on mouse move events)
        const posAtEventPixel = (evt: MapBrowserEvent<UIEvent>) => {
            // Get the pixel at position and extract features
            const pixel  = this._map!.getEventPixel(evt.originalEvent);
            const lonlat = transform(evt.coordinate, _PROJECTION, 'EPSG:4326');
            const hdms   = CoordinatesUtils.decimalToDegreesAndMinutes(lonlat);
            // Handle crossing the (0,0)
            var   lon  = Math.abs(lonlat[0]);
            const sign = Math.sign(lonlat[0]);
            while (lon > 180) { lon = lon - 360; };
            return {
                left:  `${pixel[0]}px`,
                top:   `${pixel[1]}px`,
                lon:   hdms.lon,
                lat:   hdms.lat,
                x:     lon * sign,
                y:     lonlat[1],
            };
        };

        const namesAtEventPixel = (evt: MapBrowserEvent<UIEvent>) => {
            const pixel  = this._map!.getEventPixel(evt.originalEvent);
            const names: string[] = [];
            try {
                this._map!.forEachFeatureAtPixel(pixel, (feature, layer) => {
                    if (feature && layer) {
                        const name = feature.get('name');
                        if (name) {
                            names.push(name);
                        }
                    }
                }, { hitTolerance: 7.4 });
            }
            catch(err) {
                console.warn("Could not fetch pixel features: ", err);
            }
            return names;
        };

        const infoAtEventPixel = (evt: MapBrowserEvent<UIEvent>) => {
            const pixel  = this._map!.getEventPixel(evt.originalEvent);
            const info: {[index:string]: any} = {};
            try {
                this._map!.forEachFeatureAtPixel(pixel, (feature: Feature<Geometry>|RenderFeature, layer) => {
                    if (feature && layer && (<Feature<Geometry>>feature).getKeys !== undefined) {
                        // Extract properties other than geometry (object)
                        // Flattened when multiple layers are traversed but otherwise cluttered to display
                        (<Feature<Geometry>>feature).getKeys().filter(k => k != 'geometry').forEach(k => info[k] = feature.get(k));
                        info['layer'] = layer.get('title');
                    }
                });
            }
            catch(err) {
                console.warn("Could not fetch pixel features: ", err);
            }
            return info;
        };

        this._map.on('pointermove', (evt: MapBrowserEvent<UIEvent>) => {
            if (evt.dragging) {
                infoBox.style.display = "none";
                return;
            }
            // Process every 3 move events only
            this._moveCount++;
            if (this._moveCount != 3) { return; }
            this._moveCount = 0;

            const pos   = posAtEventPixel(evt);
            const names = namesAtEventPixel(evt);
            
            if (names.length) {
                const uniqueArray = [...new Set(names)];
                this._infotext         = uniqueArray;
                infoBox.style.left    = pos.left;
                infoBox.style.top     = pos.top;
                infoBox.style.display = "block";
            } else if (this._infotext.length) {
                this._infotext = [];
                infoBox.style.display = "none";
            }
            // Send the coordinates in an event for display (temp solution for port coord verification)
            this.dispatchEvent(new CustomEvent('mapcoordmove', {bubbles: true, composed: true, detail: { pos: pos }}));
        });

        this._map.on("movestart", () => {
            this._map?.setProperties({"isMoving": true})
        })

        this._map.on("moveend", () => {
            this._map?.setProperties({"isMoving": false})
        })

        this._map.on('click', evt => {
            const pos  = posAtEventPixel(evt) ;
            const info = infoAtEventPixel(evt) ;
            
            // Shift key is for selecting vessel for weather broadcasting
            // so return early to skip selecting vessel for highlight
            if (evt.originalEvent.shiftKey) {
                return;
            }
            
            // Send the coordinates in an event for display (temp solution for port coord verification)
            this.dispatchEvent(new CustomEvent('mapcoord', {bubbles: true, composed: true, detail: { pos: pos, info: info }}));

            const currentImoNo = UiStateModel!.getValue(UiStateModelParam.IMO);

            if (!Object.keys(info).length && currentImoNo) {
                return;
            }
            // deselect vessel clicked if it is the current one selected
            if ((info.imoNo && currentImoNo && currentImoNo == info.imoNo) && info.nameFocus!=="focus") {
                UiStateModel!.setValue(UiStateModelParam.IMO);
                UiStateModel!.setValue(UiStateModelParam.FISubView);
            } else if (info.imoNo && currentImoNo && (currentImoNo != info.imoNo) || (info.imoNo && !currentImoNo)) {
                UiStateModel!.setValue(UiStateModelParam.IMO, info.imoNo);
                UiStateModel!.setValue(UiStateModelParam.FISubView, FiSubView.VesselDetails);
                UiStateModel!.setLeftPanelVisibility(true,"hide-anchor");
                UiStateModel!.setShowAnchor(true);
                TraceService!.event(
                    FI_EVENT_CATEGORIES.MAP_INTERACTIONS,
                    MAP_INTERACTIONS.SELECTED_VESSEL,
                    info.imoNo
                );
            }
        });
    }

    zoomIn() {
        const zoom = this._mapView?.getZoom();
        if (zoom) {
            this._mapView!.animate({zoom: zoom + 1, duration: this._zoomDuration});
        }
    }

    zoomOut() {
        const zoom = this._mapView?.getZoom();
        if (zoom) {
            this._mapView!.animate({zoom: zoom - 1, duration: this._zoomDuration});
        }
    }

    centerOn(position: Coordinate, instant?: boolean) {
        if (instant) {
            this._mapView!.setCenter(fromLonLat(position, _PROJECTION));
        } else {
            // Only animate if new position is far enough from current position (currently more than 1/4th of the map displayed)
            const currentCenter = this._mapView?.getCenter();
            if (currentCenter) {
                const newCenter = fromLonLat(position, _PROJECTION);
                const xDelta = Math.abs(currentCenter[0] - newCenter[0]);
                const yDelta = Math.abs(currentCenter[1] - newCenter[1]);
                const viewPort = this._mapView!.calculateExtent([this._mapElement!.clientWidth, this._mapElement!.clientHeight]);

                if (xDelta > Math.abs(viewPort[2] - viewPort[0])/4 || yDelta > Math.abs(viewPort[3] - viewPort[1])/4) {
                    this._mapView!.animate({center: newCenter, duration: this._centerDuration}, () => this._map?.dispatchEvent(CustomMapEvents.EndOfAnimation));
                } else {
                    this._mapView!.setCenter(newCenter);
                }
            }
        }
    }

    getMap() {
        return this._map;
    }

    firstUpdated() {
        this._mapElement = this.shadowRoot!.querySelector('#map');
    }

    @property({attribute: false}) private _mapStyles: string = "";
    @property({attribute: false}) private _infotext: string[] = [];
    private _moveCount: number = 0;
    private _mapAt: MapAtInfo = { latitude: 0, longitude: 0, zoom: 4 };
    private _zoomDuration: number = 250;
    private _centerDuration: number = 250;
    private _mapView: View|null = null;
    private _map: MGMap|null = null;
    private _mapElement: HTMLElement|null = null;
    private _voyageLayers: VoyageLayers|null = null;

    private async _fetchMapStyle() {
        // TODO: Replace this with our own styles for the controls in the map we use.
        let res = await fetch('./assets/styles/ol.css');  // PATH in build?
        this._mapStyles = await res.text();
    }
};
